// SearchOverviewPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { BringzzPageContainer, BringzzInput, BringzzIcon, BringzzText, BringzzSelect, BringzzDropdown, BringzzButton, Button } from "@bringzz/components";
import HeaderDefault from './HeaderDefault';
import Categories from './Categories';
import ScanCTA from './ScanCTA';
import GeneralOptions from './GeneralOptions';
import ProductGroups from './ProductGroups';
import ProductCategories from './ProductCategories';
import SearchFooter from './SearchFooter';
import SearchModal from './SearchModal';
import { getCategories, alphabets } from './constants';
import classNames from 'classnames';
import HeaderCategory from './HeaderCategory';
import HeaderInput from './HeaderInput';
import useRequest from 'hooks/useRequest';
import { useDebounce } from '@bringzz/hooks';
import { useBasket } from 'context/BasketContext';
import { toast } from 'react-toastify';
import DefaultProductImage from 'assets/DefaultProductImage.png';

import Companies from './Companies';
import CategoryShimmer from './components/CategoryShimmer';
import { useTranslation } from 'react-i18next';
import { isPharmacyOpen } from 'utils/pharmacyUtils';

const INITIAL_HEADER_HEIGHT = 214;
const SHRINK_SPEED = 1;
const PRODUCTS_PER_PAGE = 50;

const SearchOverviewPage = () => {
    const { t } = useTranslation();
    const sortOptions = [
        { value: 'name_asc', label: t('component.search.sort.name_asc') },
        { value: 'name_desc', label: t('component.search.sort.name_desc') },
        { value: 'price_asc', label: t('component.search.sort.price_asc') },
        { value: 'price_desc', label: t('component.search.sort.price_desc') },
        { value: 'pzn', label: 'PZN' },
    ];

    const { backPress, navigate } = useNavigation();
    const [searchQuery, setSearchQuery] = useState("");
    const [headerHeight, setHeaderHeight] = useState(INITIAL_HEADER_HEIGHT);
    const [lastScrollTop, setLastScrollTop] = useState(0);
    const [MIN_HEADER_HEIGHT, SET_MIN_HEADER_HEIGHT] = useState(48)
    const [isHeaderOpen, setIsHeaderOpen] = useState(true);
    const [searchData, setSearchData] = useState(null)
    const [alphabetFilter, setAlphabetFilter] = useState('All')
    const { sendRequest, data, error, loading } = useRequest();
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();
    const isLoadingMore = useRef(false);
    const [sortBy, setSortBy] = useState('');
    const [filters, setFilters] = useState({});
    const debouncedSearchTerm = useDebounce(searchQuery, 300);
    const { addToBasket } = useBasket();
    const [loadingProductId, setLoadingProductId] = useState(null);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [companyFilter, setCompanyFilter] = useState(null);
    const [localPharmaciesCategory, setLocalPharmaciesCategory] = useState(null);
    const [loadingPharmacies, setLoadingPharmacies] = useState(false);
    
    // Combine static categories with dynamic local pharmacies
    const allCategories = React.useMemo(() => {
        const staticCategories = getCategories(t);
        if (localPharmaciesCategory) {
            // Replace the placeholder local pharmacies category with the loaded one
            return staticCategories.map(category => 
                category.isPharmacyList ? localPharmaciesCategory : category
            );
        }
        return staticCategories || [];
    }, [localPharmaciesCategory, t]);
    const [symptoms, setSymptoms] = useState([]);

    const lastProductElementRef = useCallback(node => {
        if (loading || isLoadingMore.current) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !isLoadingMore.current) {
                isLoadingMore.current = true;
                setPage(prevPage => prevPage + 1);
            }
        }, {
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const handleScroll = () => {
        const scrollTop = window.scrollY;
        const scrollDifference = scrollTop - lastScrollTop;

        if (scrollDifference > 0) {
            const newHeight = Math.max(MIN_HEADER_HEIGHT, headerHeight - scrollDifference * SHRINK_SPEED);
            setHeaderHeight(newHeight);
        } else if (scrollTop < 170 && scrollDifference < 0) {
            const newHeight = Math.min(INITIAL_HEADER_HEIGHT, headerHeight - scrollDifference * SHRINK_SPEED);
            setHeaderHeight(newHeight);
        }

        setLastScrollTop(scrollTop);
    };

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [lastScrollTop, headerHeight]);

    const toggleHeaderHeight = () => {
        setIsHeaderOpen(prevState => !prevState);
        setHeaderHeight(prevHeight =>
            prevHeight === INITIAL_HEADER_HEIGHT ? MIN_HEADER_HEIGHT : INITIAL_HEADER_HEIGHT
        );
    };

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
        setPage(1);
        setHasMore(true);
    };

    const formatPrice = (price) => {
        return price ? `€${parseFloat(price).toFixed(2)}` : t('component.search.price.not_available');
    };

    useEffect(() => {
        if (debouncedSearchTerm.trim() !== '') {
            setPage(1);
            setHasMore(true);
        } else {
            setSearchData(null);
            setPage(1);
            setHasMore(true);
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const keyword = params.get('category');

        if (keyword) {
            setPage(1);
            setHasMore(true);
        }
    }, [location.search]);

    const handleAddToBasket = async (product) => {
        try {
            setLoadingProductId(product.id || product.pzn);
            await addToBasket(product);
            toast.success(t('component.search.toast.product_added', { name: product.name }));
        } catch (error) {
            console.error('Error adding product to basket:', error);
            toast.error(t('component.search.toast.add_failed'));
        } finally {
            setLoadingProductId(null);
        }
    };

    const filterPharmaciesByAlphabet = (pharmacies, filter) => {
        if (!pharmacies) return [];
        return pharmacies.filter((pharmacy) => {
            if (filter === 'All' || filter === '#') {
                return true;
            } else {
                return pharmacy.label.toUpperCase().startsWith(filter);
            }
        });
    };

    const handlePharmacyClick = (pharmacy) => {
        if (pharmacy.integrationId) {
            navigate(`/pharmacy/profile/${pharmacy.integrationId}`);
        }
    };

    const handleCompanyProductsClick = (companyId, companyName) => {
        setCompanyFilter({ id: companyId, name: companyName });
        setIsSearchModalOpen(true);
    };

    const handleManufacturerProductsClick = (manufacturerId, manufacturerName) => {
        setCompanyFilter({ id: manufacturerId, name: manufacturerName });
        setIsSearchModalOpen(true);
    };

    const loadLocalPharmacies = async () => {
        if (localPharmaciesCategory) return;
        
        setLoadingPharmacies(true);
        try {
            const response = await sendRequest({
                method: 'GET',
                url: '/module/pharmacy/getLocalPharmacies'
            });

            if (response.status === 'success' && response.data) {
                const formattedPharmacies = response.data.map(pharmacy => {
                    const nightDutyInterval = pharmacy.night_duty_interval || 5;
                    const status = isPharmacyOpen(pharmacy.opening_hours);
                    
                    return {
                        label: pharmacy.name,
                        id: pharmacy.id,
                        integrationId: pharmacy.integrationId,
                        email: pharmacy.email,
                        telefon: pharmacy.telefon,
                        fax: pharmacy.fax,
                        pharmacyPic: pharmacy.pharmacyPic || null,
                        opening_hours: pharmacy.opening_hours,
                        address: pharmacy.address,
                        city: pharmacy.address?.city_name || null,
                        distance: pharmacy.distance || null,
                        isOpen: status,
                        statusText: status ? t('component.search.pharmacy.open') : t('component.search.pharmacy.closed'),
                        statusColor: status ? 'text-green-600' : 'text-red-600'
                    };
                });

                const formattedCategory = {
                    name: t('component.search.categories.local_pharmacies.name') || 'Local Pharmacies',
                    shortName: t('component.search.categories.local_pharmacies.short') || 'Local Pharmacies',
                    icon: <BringzzIcon size="34" icon="IconPharmacyAlt" folder="HealthIcons" />,
                    items: formattedPharmacies,
                    isPharmacyList: true
                };
                setLocalPharmaciesCategory(formattedCategory);
                
                setSearchData({
                    method: "category",
                    value: formattedCategory.shortName,
                    results: formattedPharmacies
                });
            }
        } catch (error) {
            console.error('Error fetching local pharmacies:', error);
        } finally {
            setLoadingPharmacies(false);
        }
    };

    const renderPharmacyList = () => {
        if (!searchData) return null;
        
        if (loadingPharmacies || searchData.loading) {
            return (
                <div className="flex items-center justify-center p-8">
                    <BringzzIcon
                        size="32"
                        icon="IconSpinner"
                        folder="LineIcons"
                        className="animate-spin text-magic-lilac"
                    />
                </div>
            );
        }
        
        if (!searchData.results) return null;

        const filteredPharmacies = filterPharmaciesByAlphabet(searchData.results, alphabetFilter);

        return (
            <div className='rounded-xl bg-white mx-2 py-2'>
                <div className='flex items-center overflow-x-auto sticky top-0 bg-white z-10 py-2' style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                    <BringzzText tag='h3' className='whitespace-nowrap mr-1'>{t('component.search.general.alphabet_filter')}</BringzzText>
                    <div className='flex space-x-3 sm:space-x-2 min-w-0'>
                        {alphabets.map((char, index) => {
                            const isSelected = char === alphabetFilter;
                            return (
                                <div
                                    key={index}
                                    className={classNames('bg-sand p-2 rounded-full cursor-pointer flex-shrink-0',
                                        isSelected && "!bg-magic-lilac"
                                    )}
                                    onClick={() => setAlphabetFilter(char)}
                                >
                                    <BringzzText tag='h5'>{char}</BringzzText>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div>
                    {filteredPharmacies.map((pharmacy, index) => (
                        <div
                            key={pharmacy.id}
                            className={classNames(
                                'h-16 cursor-pointer flex justify-between px-6 items-center',
                                index !== filteredPharmacies.length - 1 && 'border-b',
                                'hover:bg-magic-lilac/10 transition-colors duration-300'
                            )}
                            onClick={() => handlePharmacyClick(pharmacy)}
                        >
                            <div className='flex items-center space-x-3'>
                                {pharmacy.pharmacyPic ? (
                                    <img 
                                        src={pharmacy.pharmacyPic} 
                                        alt={pharmacy.label}
                                        className='w-10 h-10 rounded-full object-cover flex-shrink-0'
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div 
                                    className={`w-10 h-10 rounded-full bg-magic-lilac flex items-center justify-center flex-shrink-0 ${pharmacy.pharmacyPic ? 'hidden' : 'flex'}`}
                                >
                                    <BringzzText tag='h5' className='text-white font-bold'>
                                        {pharmacy.label.charAt(0).toUpperCase()}
                                    </BringzzText>
                                </div>
                                <div className='flex flex-col'>
                                    <BringzzText tag='h4'>{pharmacy.label}</BringzzText>
                                <div className='flex justify-between items-center'>
                                    <div className='flex items-center space-x-2'>
                                        {pharmacy.distance && (
                                            <BringzzText tag='p' className='text-xs text-gray-500'>
                                                {pharmacy.distance}km
                                            </BringzzText>
                                        )}
                                        {pharmacy.distance && pharmacy.city && (
                                            <BringzzText tag='p' className='text-xs text-gray-400' style={{fontSize: '8px'}}>
                                                •
                                            </BringzzText>
                                        )}
                                        {pharmacy.city && (
                                            <BringzzText tag='p' className='text-xs text-gray-500'>
                                                {pharmacy.city}
                                            </BringzzText>
                                        )}
                                    </div>
                                </div>
                                    <div className='flex items-center space-x-1'>
                                        <BringzzText tag='p' className={`text-xs font-medium ${pharmacy.statusColor || (pharmacy.isOpen ? 'text-green-600' : 'text-red-600')}`}>
                                            {pharmacy.statusText || (pharmacy.isOpen ? t('component.search.pharmacy.open') : t('component.search.pharmacy.closed'))}
                                        </BringzzText>
                                        {pharmacy.statusTimeText && (
                                            <>
                                                <BringzzText tag='p' className='text-xs text-gray-400' style={{fontSize: '8px'}}>
                                                    •
                                                </BringzzText>
                                                <BringzzText tag='p' className='text-xs text-gray-500'>
                                                    {pharmacy.statusTimeText}
                                                </BringzzText>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <BringzzIcon 
                                size="16" 
                                icon="IconInfoCircle" 
                                folder="LineIcons"
                                className="text-magic-lilac" 
                            />
                        </div>
                    ))}
                    {filteredPharmacies.length === 0 && (
                        <div className='px-6 py-4'>
                            <BringzzText tag='p'>{t('component.search.pharmacy.no_pharmacies_found')}</BringzzText>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <BringzzPageContainer
            className="bg-sand"
            footerHover={true}
            footer={
                <div className='p-4' onClick={() => setIsSearchModalOpen(true)}>
                    <BringzzInput
                        className='shadow-xl border-slate-400 bg-gray-100 cursor-pointer'
                        placeholder={t('component.search.general.find_items')}
                        onChange={(e, newIsValid, newValue) => setSearchQuery(newValue)}
                        icon={{
                            left: <BringzzIcon
                                size="20"
                                icon="IconSearchAlt"
                                folder="LineIcons"
                                className="cursor-pointer"
                            />,
                        }}
                        readOnly
                    />
                </div>
            }
        >
            <div className='relative'>
                {!searchData && <HeaderDefault headerHeight={headerHeight} />}
                {searchData && searchData.method === "category" && (
                    <HeaderCategory
                        SET_MIN_HEADER_HEIGHT={SET_MIN_HEADER_HEIGHT}
                        searchData={searchData}
                        setSearchData={setSearchData}
                        headerHeight={headerHeight}
                        categories={allCategories}
                    />
                )}
                {searchData && searchData.method === "input" && (
                    <HeaderInput
                        SET_MIN_HEADER_HEIGHT={SET_MIN_HEADER_HEIGHT}
                        searchData={searchData}
                        setSearchData={setSearchData}
                        headerHeight={headerHeight}
                    />
                )}
                <div style={{ paddingTop: searchData && searchData.method === "input" ? `60px` : `${INITIAL_HEADER_HEIGHT}px` }}>
                    {loading ? (
                        <div className="flex items-center justify-center p-8">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    ) : (
                        <>
                            {!searchData && (
                                <div>
                                    {loadingPharmacies ? (
                                        <CategoryShimmer />
                                    ) : (
                                        allCategories && allCategories.length > 0 && (
                                            <Categories
                                                categories={allCategories}
                                                setSearchData={setSearchData}
                                                loadLocalPharmacies={loadLocalPharmacies}
                                                callBack={() => {
                                                    SET_MIN_HEADER_HEIGHT(100);
                                                    setHeaderHeight(190);
                                                }}
                                            />
                                        )
                                    )}
                                
                                    <ProductCategories />
                                    <ProductGroups />
                                    <ScanCTA />
                                    <GeneralOptions />
                                </div>
                            )}
                            {searchData && searchData.method === "category" && (
                                <>
                                    {localPharmaciesCategory && searchData.value === localPharmaciesCategory.shortName ? (
                                        loadingPharmacies ? (
                                            <CategoryShimmer />
                                        ) : (
                                            renderPharmacyList()
                                        )
                                    ) : searchData.results && searchData.results.length > 0 && searchData.results[0].name ? (
                                        <div className='rounded-xl bg-white mx-2 py-2'>
                                            <div className="p-4 border-b">
                                                <BringzzDropdown
                                                    options={sortOptions}
                                                    value={sortBy}
                                                    onChange={handleSortChange}
                                                    placeholder={t('component.search.general.sort_by')}
                                                />
                                            </div>
                                            {searchData.results.map((product, index) => (
                                                <div
                                                    key={index}
                                                    className="p-4 border-b flex items-center space-x-4"
                                                >
                                                    <img 
                                                        src={product.productImage || DefaultProductImage} 
                                                        alt={product.name || 'Product'}
                                                        className={`w-16 h-16 rounded-lg flex-shrink-0 cursor-pointer ${product.productImage ? 'object-cover' : 'object-contain p-2'}`}
                                                        onClick={() => navigate(`/product/details/${product.integrationId || product.pzn}`)}
                                                        onError={(e) => {
                                                            if (e.target.src !== DefaultProductImage) {
                                                                e.target.src = DefaultProductImage;
                                                                e.target.className = e.target.className.replace('object-cover', 'object-contain p-2');
                                                            }
                                                        }}
                                                    />
                                                    <div className="flex-grow">
                                                        <BringzzText tag='h4' className="font-bold">{product.name}</BringzzText>
                                                        <BringzzText tag='p' className="text-sm">{t('component.search.general.pzn')}: {product.pzn}</BringzzText>
                                                        <BringzzText tag='p' className="text-sm">{t('component.search.general.package')}: {product.productAmount} {product.packaging}</BringzzText>
                                                        <div className="mt-2 flex justify-between items-center">
                                                            <BringzzText tag='p' className="text-lg font-semibold">{formatPrice(product.price)}</BringzzText>
                                                            <BringzzButton
                                                                className="bg-magic-lilac justify-center"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAddToBasket(product);
                                                                }}
                                                                disabled={!product.price || loadingProductId === (product.productId || product.pzn)}
                                                            >
                                                                {loadingProductId === (product.productId || product.pzn) ? (
                                                                    <span>{t('component.search.actions.adding')}</span>
                                                                ) : (
                                                                    <span>{t('component.search.actions.add_to_basket')}</span>
                                                                )}
                                                            </BringzzButton>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className='rounded-xl bg-white mx-2 py-2'>
                                            <div className='flex items-center px-4 space-x-3 sm:space-x-2 overflow-x-auto'>
                                                <BringzzText tag='h3' className='whitespace-nowrap mr-1'>{t('component.search.general.alphabet_filter')}</BringzzText>
                                                {alphabets.map((char, index) => (
                                                    <div
                                                        key={index}
                                                        className={classNames('bg-sand p-2 rounded-full cursor-pointer flex-shrink-0',
                                                            char === alphabetFilter && "!bg-magic-lilac"
                                                        )}
                                                        onClick={() => setAlphabetFilter(char)}
                                                    >
                                                        <BringzzText tag='h5'>{char}</BringzzText>
                                                    </div>
                                                ))}
                                            </div>
                                            {(searchData.value === 'Companies' || searchData.value === t('component.search.categories.companies.short')) ? (
                                                <Companies 
                                                    selectedLetter={alphabetFilter}
                                                    setSearchData={setSearchData} 
                                                    callBack={() => {
                                                        SET_MIN_HEADER_HEIGHT(100);
                                                        setHeaderHeight(190);
                                                    }}
                                                    onCompanyProductsClick={handleCompanyProductsClick}
                                                />
                                            ) : (
                                                getCategories(t).filter((category) => category.shortName === searchData.value)[0]?.items
                                                    ?.filter((item) => {
                                                        if (alphabetFilter === 'All' || alphabetFilter === '#') {
                                                            return true;
                                                        } else {
                                                            return item.label.startsWith(alphabetFilter);
                                                        }
                                                    })
                                                    .map((item, index, filteredArray) => (
                                                        <div key={index} className={classNames(
                                                            'h-12 cursor-pointer flex justify-between px-6 items-center',
                                                            index !== filteredArray.length - 1 && 'border-b'
                                                        )}>
                                                            <div className='flex space-x-1 items-center'>
                                                                <BringzzText tag='h4'>{item.label.split("*")[0]}</BringzzText>
                                                                <div>&bull;</div>
                                                                <BringzzText tag='h4'>{item.label.split("*")[1]}</BringzzText>
                                                            </div>
                                                            <BringzzIcon
                                                                size="20"
                                                                icon="IconChevronRight"
                                                                folder="LineIcons"
                                                                className="cursor-pointer"
                                                            />
                                                        </div>
                                                    ))
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            {searchData && searchData.method === "input" && (
                                <div className='rounded-xl bg-white mx-2 py-2'>
                                    <div className="p-4 border-b">
                                        <BringzzDropdown
                                            options={sortOptions}
                                            value={sortBy}
                                            onChange={handleSortChange}
                                            placeholder={t('component.search.general.sort_by')}
                                        />
                                    </div>
                                    {searchData.results && searchData.results.length > 0 ? (
                                        searchData.results.map((product, index) => (
                                            <div
                                                key={index}
                                                className="p-4 border-b flex items-center space-x-4"
                                                ref={index === searchData.results.length - 1 ? lastProductElementRef : null}
                                            >
                                                <img 
                                                    src={product.productImage || DefaultProductImage} 
                                                    alt={product.name || 'Product'}
                                                    className={`w-16 h-16 rounded-lg flex-shrink-0 cursor-pointer ${product.productImage ? 'object-cover' : 'object-contain p-2'}`}
                                                    onClick={() => navigate(`/product/details/${product.integrationId || product.pzn}`)}
                                                    onError={(e) => {
                                                        if (e.target.src !== DefaultProductImage) {
                                                            e.target.src = DefaultProductImage;
                                                            e.target.className = e.target.className.replace('object-cover', 'object-contain p-2');
                                                        }
                                                    }}
                                                />
                                                <div className="flex-grow">
                                                    <BringzzText tag='h4' className="font-bold">{product.name}</BringzzText>
                                                    <BringzzText tag='p' className="text-sm">{t('component.search.general.pzn')}: {product.pzn}</BringzzText>
                                                    <BringzzText tag='p' className="text-sm">{t('component.search.general.package')}: {product.packSize} {product.packSizeUnit}</BringzzText>
                                                    <div className="mt-2 flex justify-between items-center">
                                                        <BringzzText tag='p' className="text-lg font-semibold">{formatPrice(product.price)}</BringzzText>
                                                        <BringzzButton
                                                            className="bg-magic-lilac justify-center"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleAddToBasket(product);
                                                            }}
                                                            disabled={!product.price || loadingProductId === (product.id || product.pzn)}
                                                        >
                                                            {loadingProductId === (product.id || product.pzn) ? (
                                                                <span>{t('component.search.actions.adding')}</span>
                                                            ) : (
                                                                <span>{t('component.search.actions.add_to_basket')}</span>
                                                            )}
                                                        </BringzzButton>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-4">{t('component.search.empty.no_products')}</div>
                                    )}
                                    {loading && (
                                        <div className="flex items-center justify-center p-4">
                                            <BringzzIcon
                                                size="24"
                                                icon="IconSpinner"
                                                folder="LineIcons"
                                                className="animate-spin text-magic-lilac"
                                            />
                                        </div>
                                    )}
                                    {error && <div className="p-4 text-red-500">{t('component.search.general.error')}: {error.message}</div>}
                                </div>
                            )}
                            {searchData && searchData.method === 'symptom' && (
                                <div className='rounded-xl bg-white mx-2 py-2'>
                                    <div className='flex items-center px-4 space-x-2 overflow-x-auto '>
                                        <BringzzText tag='h3' className='whitespace-nowrap mr-1'>{t('component.search.symptom.products_for')} {searchData.value}</BringzzText>
                                    </div>
                                    {searchData.results && searchData.results.length > 0 ? (
                                        searchData.results.map((product, index) => (
                                            <div
                                                key={index}
                                                className="p-4 border-b flex items-center space-x-4 cursor-pointer"
                                                onClick={() => navigate(`/product/details/${product.integrationId || product.pzn}`)}
                                            >
                                                <img 
                                                    src={product.productImage || DefaultProductImage} 
                                                    alt={product.name || 'Product'}
                                                    className={`w-16 h-16 rounded-lg flex-shrink-0 ${(product.productImage) ? 'object-cover' : 'object-contain p-2'}`}
                                                    onError={(e) => {
                                                        if (e.target.src !== DefaultProductImage) {
                                                            e.target.src = DefaultProductImage;
                                                            e.target.className = e.target.className.replace('object-cover', 'object-contain p-2');
                                                        }
                                                    }}
                                                />
                                                
                                                <div className="flex-grow">

                                                    <BringzzText tag='h4' className="font-bold">{product.name}</BringzzText>
                                                    <BringzzText tag='p' className="text-sm">{t('component.search.general.pzn')}: {product.pzn}</BringzzText>
                                                    <BringzzText tag='p' className="text-sm">{t('component.search.general.package')}: {product.packSize} {product.packSizeUnit}</BringzzText>
                                                    <div className="mt-2 flex justify-between items-center">
                                                        <BringzzText tag='p' className="text-lg font-semibold">{formatPrice(product.price)}</BringzzText>
                                                        <BringzzButton
                                                            className="bg-magic-lilac justify-center"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleAddToBasket(product);
                                                            }}                                                        
                                                            disabled={!product.price || loadingProductId === (product.id || product.pzn)}
                                                        >
                                                            {loadingProductId === (product.id || product.pzn) ? (
                                                                <span>{t('component.search.actions.adding')}</span>
                                                            ) : (
                                                                <span>{t('component.search.actions.add_to_basket')}</span>
                                                            )}
                                                        </BringzzButton>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-4">{t('component.search.symptom.no_products_found')}</div>
                                    )}
                                </div>
                            )}
                            <SearchFooter />
                        </>
                    )}
                </div>
            </div>
            <SearchModal 
                isOpen={isSearchModalOpen} 
                onClose={() => {
                    setIsSearchModalOpen(false);
                    setCompanyFilter(null);
                }}
                companyId={companyFilter?.id}
                companyName={companyFilter?.name}
            />
        </BringzzPageContainer>
    );
};

export default SearchOverviewPage;